// BankPLTable.js — P&L table with lines per spec.
// Monthly and cumulative views. Break-even indicator row.

import React, { useState } from 'react';

function formatCurrency(value) {
  if (value == null || isNaN(value)) return '-';
  const num = parseFloat(value);
  const formatted = Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  if (num < 0) return `(${formatted})`;
  return formatted;
}

const LINE_LABELS = {
  fx_spread: 'FX Spread Income',
  forward_premium: 'Forward Premium Income',
  hedge_cost: 'Hedge Cost',
  settlement_fees: 'Settlement Fees',
  revenue_share: 'Revenue Share (to Forwardsflow)',
  net: 'Net Revenue',
};

const LINE_ORDER = ['fx_spread', 'forward_premium', 'hedge_cost', 'settlement_fees', 'revenue_share', 'net'];

export default function BankPLTable({ monthlyPL = [], summary = {} }) {
  const [view, setView] = useState('monthly'); // monthly | cumulative

  if (!monthlyPL.length) {
    return (
      <div className="text-xs text-gray-400 text-center py-8">
        No P&L data available
      </div>
    );
  }

  // Build cumulative view
  const cumulativePL = [];
  let cumFx = 0, cumFwd = 0, cumHedge = 0, cumSettle = 0, cumRev = 0;
  for (const month of monthlyPL) {
    cumFx += month.fx_spread_income;
    cumFwd += month.forward_premium_income;
    cumHedge += month.hedge_cost;
    cumSettle += month.settlement_fees;
    cumRev += month.revenue_share;

    cumulativePL.push({
      month: month.month,
      fx_spread_income: cumFx,
      forward_premium_income: cumFwd,
      hedge_cost: cumHedge,
      settlement_fees: cumSettle,
      revenue_share: cumRev,
      net_revenue: cumFx + cumFwd - cumHedge + cumSettle - cumRev,
    });
  }

  const displayData = view === 'cumulative' ? cumulativePL : monthlyPL;

  return (
    <div>
      {/* View toggle */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          P&L Breakdown
        </h3>
        <div className="inline-flex bg-gray-100 rounded-md p-0.5 text-[10px]">
          <button
            onClick={() => setView('monthly')}
            className={`px-2 py-1 rounded ${view === 'monthly' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}
          >
            Monthly
          </button>
          <button
            onClick={() => setView('cumulative')}
            className={`px-2 py-1 rounded ${view === 'cumulative' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}
          >
            Cumulative
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className={`rounded-lg p-3 ${summary.net_revenue >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
          <p className="text-[10px] text-gray-500 uppercase">Net Revenue</p>
          <p className={`text-sm font-semibold font-mono ${summary.net_revenue >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
            ${formatCurrency(summary.net_revenue)}
          </p>
        </div>
        <div className="rounded-lg p-3 bg-gray-50">
          <p className="text-[10px] text-gray-500 uppercase">Break-even</p>
          <p className="text-sm font-semibold font-mono text-gray-800">
            {summary.break_even_forward_bps ? `${parseFloat(summary.break_even_forward_bps).toFixed(1)} bps` : '-'}
          </p>
        </div>
        <div className="rounded-lg p-3 bg-gray-50">
          <p className="text-[10px] text-gray-500 uppercase">Positions</p>
          <p className="text-sm font-semibold font-mono text-gray-800">
            {summary.position_count || '-'}
          </p>
        </div>
      </div>

      {/* P&L table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-2 text-gray-500 font-medium sticky left-0 bg-white">Line</th>
              {displayData.map(m => (
                <th key={m.month} className="text-right py-2 px-2 text-gray-500 font-medium whitespace-nowrap">
                  {m.month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {LINE_ORDER.map(lineType => (
              <tr
                key={lineType}
                className={`border-b ${lineType === 'net' ? 'border-gray-300 font-semibold bg-gray-50' : 'border-gray-50'}`}
              >
                <td className={`py-1.5 px-2 sticky left-0 ${lineType === 'net' ? 'bg-gray-50 font-semibold' : 'bg-white'} text-gray-700`}>
                  {LINE_LABELS[lineType]}
                </td>
                {displayData.map(m => {
                  const value = lineType === 'fx_spread' ? m.fx_spread_income
                    : lineType === 'forward_premium' ? m.forward_premium_income
                    : lineType === 'hedge_cost' ? -m.hedge_cost
                    : lineType === 'settlement_fees' ? m.settlement_fees
                    : lineType === 'revenue_share' ? -m.revenue_share
                    : m.net_revenue;

                  return (
                    <td
                      key={m.month}
                      className={`py-1.5 px-2 text-right font-mono ${
                        lineType === 'net'
                          ? (value >= 0 ? 'text-emerald-700' : 'text-red-700')
                          : value < 0 ? 'text-red-600' : 'text-gray-700'
                      }`}
                    >
                      {formatCurrency(value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
